import React, { useState, useContext } from 'react'
import { store } from '../../stores/admin_templates'
import { Badge, Button, DropdownItem, DropdownMenu, DropdownToggle, FormGroup, Input, Spinner, UncontrolledDropdown } from 'reactstrap'
import MultiformatIcon from '../shared/MultiformatIcon'
import { ChevronDown, Maximize2, Play, Trash2 } from 'react-feather'
import PreviewModal from '../graphics/PreviewModal'

export default function TemplateRow({ template }) {
  const { basepath, human_name: humanName, toggling, actions, dispatch } = useContext(store)
  const [previewIsOpen, setPreviewIsOpen] = useState(false)
  const {
    preview_urls,
    formats,
    title,
    generating,
    tierName,
    editor_url,
    template_filename,
    features,
    active,
    activated_at,
  } = template.attributes
  const format = formats[0]

  // Calculate explicit dimensions to prevent layout shift while maintaining aspect ratio
  const calculateImageDimensions = (width, height) => {
    const maxSize = 130
    const aspectRatio = width / height
    
    if (width <= maxSize && height <= maxSize) {
      return { width, height }
    }
    
    if (aspectRatio > 1) {
      // Landscape - width is larger
      return {
        width: maxSize,
        height: Math.round(maxSize / aspectRatio)
      }
    } else {
      // Portrait or square - height is larger or equal
      return {
        width: Math.round(maxSize * aspectRatio),
        height: maxSize
      }
    }
  }

  const imageDimensions = calculateImageDimensions(format.width, format.height)

  const open = () => setPreviewIsOpen(true)
  const close = () => setPreviewIsOpen(false)

  const deleteTemplate = () => {
    dispatch({type: actions.TOGGLE_DELETE_MODAL, payload: template })
  }

  return (
    (
      <tr key={template.id}>
        <td width="1">
          {generating ? (
              <Spinner color="primary" />
          ) : (
            Object.values(preview_urls).length > 0 && Object.values(preview_urls)[0] ? (
              <div className="position-relative">
                <img 
                  src={Object.values(preview_urls)[0]} 
                  alt={title} 
                  className="template-preview" 
                  width={imageDimensions.width}
                  height={imageDimensions.height}
                  style={{ aspectRatio: `${format.width} / ${format.height}` }} 
                />
                <Button
                  color="player-control"
                  className="graphic-preview-control graphic-preview-control-sm p-1"
                  style={{ aspectRatio: formats.length == 1 ? "1 / 1" : null }}
                  onClick={open}
                >
                  {formats.length > 1 && <MultiformatIcon formats={formats} />}
                  {formats[0].extension == "mp4" && <Play size="20" />}
                  {formats[0].extension != "mp4" && formats.length == 1 && (
                    <Maximize2 size="20" />
                  )}
                </Button>
                <PreviewModal graphic={template} isOpen={previewIsOpen} close={close} />
              </div>
            ) : (
              <small className="text-italic text-muted">No preview</small>
            )
          )}
        </td>
        <td className="ps-0">
          <strong className="text-dark">{title}</strong>
          <Badge size="sm" color="primary" className="ms-2 icon-valign">{tierName}</Badge><br />
          { editor_url && (
            <small className="mb-1 d-block"><a href={editor_url} className="text-muted">{template_filename}</a></small>
          )}
          <div>
            { features.events == 'single' && <Badge color="light" className="text-bg-light border me-1" size="sm">Single Event</Badge> }
            { features.events == 'multi' && <Badge color="light" className="text-bg-light border me-1" size="sm">Multiple Events</Badge> }
            { features.opponents && <Badge color="light" className="text-bg-light border me-1" size="sm">Opponents</Badge> }
            { features.players && <Badge color="light" className="text-bg-light border me-1" size="sm">Players</Badge> }
          </div>
        </td>
        <td width="1" className="pe-0 text-nowrap">
          {active && <span className="text-muted">{new Date(activated_at).toLocaleDateString()}</span>}
        </td>
        <td width="1" className="pe-0 text-nowrap">
          <FormGroup switch>
            <Input
              type="switch"
              checked={active}
              disabled={toggling == template.id}
              onChange={() => {
                dispatch({ type: actions.TOGGLE_ACTIVE, payload: { id: template.id, active: !active } })
              }}
            />
          </FormGroup>
        </td>
        <td className="text-end text-nowrap" width="1">
          <UncontrolledDropdown group>
            <Button
              color="outline-secondary"
              href={`${basepath}/${template.id}/details`}
              size="sm"
              className="d-flex align-items-center"
            >
              Edit Details
            </Button>
            <Button
              color="outline-secondary"
              href={`${basepath}/${template.id}/edit`}
              size="sm"
              className="d-flex align-items-center"
            >
              Set Defaults
            </Button>
            <DropdownToggle
              size="sm"
              color="outline-secondary"
              className="px-1 rounded-end-2"
            >
              <ChevronDown size={16} className="dropdown-indicator" />
            </DropdownToggle>
            <DropdownMenu>
              <DropdownItem
                className="text-danger d-flex gap-2 align-items-center"
                onClick={() => deleteTemplate()}
              >
                <Trash2 size={16} /> Delete
              </DropdownItem>
            </DropdownMenu>
          </UncontrolledDropdown>
        </td>
      </tr>
    )
  )
}
